import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  IconButton,
  TextField,
  Avatar,
  Fade,
  CircularProgress,
  Typography,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import { useClearProductChat, useSendProductChatMessage } from 'hooks/useChat';
import * as chatService from 'services/chatService';
import { ProductChatMessage } from 'services/chatService';
import { snackbarService } from 'services/snackbarService';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isStreaming?: boolean;
  // A message of the person's that was turned down, shown as a red notice
  // rather than as Sila's reply.
  isBlocked?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── Markdown renderer ───────────────────────────────────────────────────────
// Supports ***italic bold***, **bold**, *italic*, [link](url) (bold),
// `- ` bullets, `1. ` numbered lists, blank-line gaps.

function parseInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Order matters: *** before ** before * so longer tokens win.
  // Group 1: ***italic bold***  |  Group 2: **bold**  |  Group 3+4: [label](url)  |  Group 5: *italic*
  const regex = /\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)|\*(.+?)\*/g;
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(<span key={key++}>{text.slice(last, match.index)}</span>);
    }

    if (match[1] !== undefined) {
      // ***italic bold*** — recurse so links/bold/italic inside are rendered
      nodes.push(<em key={key++} style={{ fontStyle: 'italic', fontWeight: 600 }}>{parseInline(match[1])}</em>);
    } else if (match[2] !== undefined) {
      // **bold** — recurse so links/italic inside are rendered
      nodes.push(<span key={key++} style={{ fontWeight: 600 }}>{parseInline(match[2])}</span>);
    } else if (match[3] !== undefined) {
      // [label](url) — bold link; label may contain *italic* / **bold** / ***italic bold***
      nodes.push(
        <a
          key={key++}
          href={match[4]}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontWeight: 600, color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
        >
          {parseInline(match[3])}
        </a>
      );
    } else {
      // *italic*
      nodes.push(<em key={key++} style={{ fontStyle: 'italic' }}>{match[5]}</em>);
    }

    last = match.index + match[0].length;
  }

  if (last < text.length) {
    nodes.push(<span key={key++}>{text.slice(last)}</span>);
  }

  return nodes;
}

function renderMarkdown(text: string) {
  return text.split('\n').map((line, idx) => {
    // Empty line → small vertical gap
    if (!line.trim()) {
      return <Box key={idx} sx={{ height: '0.45em' }} />;
    }

    // Bullet: "- text"
    const bulletMatch = line.match(/^-\s+(.+)$/);
    if (bulletMatch) {
      return (
        <Box key={idx} display="flex" gap="5px">
          <span style={{ flexShrink: 0 }}>•</span>
          <span>{parseInline(bulletMatch[1])}</span>
        </Box>
      );
    }

    // Numbered list: "1. text"
    const numMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (numMatch) {
      return (
        <Box key={idx} display="flex" gap="5px">
          <span style={{ flexShrink: 0, minWidth: 16 }}>{numMatch[1]}.</span>
          <span>{parseInline(numMatch[2])}</span>
        </Box>
      );
    }

    // Regular line
    return <Box key={idx}>{parseInline(line)}</Box>;
  });
}

// ─── Typing indicator ────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <Box display="flex" alignItems="center" gap={0.5} px={1.5} py={1}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: colors.secondary,
            animation: 'bounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes bounce': {
              '0%, 80%, 100%': { transform: 'translateY(0)' },
              '40%': { transform: 'translateY(-6px)' },
            },
          }}
        />
      ))}
    </Box>
  );
}

// ─── Message bubble ──────────────────────────────────────────────────────────

interface BubbleProps {
  message: Message;
}

function MessageBubble({ message }: BubbleProps) {
  const isUser = message.sender === 'user';
  const isBlocked = !!message.isBlocked;

  return (
    <Fade in timeout={300}>
      <Box
        display="flex"
        flexDirection={isUser ? 'row-reverse' : 'row'}
        mb={1.5}
      >
        {/* Bubble */}
        <Box
          sx={{
            maxWidth: '88%',
            px: 1.5,
            py: 1,
            borderRadius: isUser
              ? '14px 14px 4px 14px'
              : '14px 14px 14px 4px',
            bgcolor: isUser ? colors.primary : isBlocked ? 'rgba(247,64,60,0.08)' : colors.topBackground,
            border: isBlocked ? `1px solid ${colors.error}` : 'none',
            boxShadow: isBlocked ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <Typography
            component="div"
            dir="auto"
            sx={{
              fontSize: 15,
              fontWeight: 400,
              color: isUser ? colors.white : isBlocked ? colors.error : colors.text,
              lineHeight: 1.5,
              wordBreak: 'break-word',
            }}
          >
            {renderMarkdown(message.text)}
          </Typography>
          {!message.isStreaming && (
            <CustomText
              text={message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              fontSize={10}
              sx={{
                color: isUser ? 'rgba(255,255,255,0.65)' : colors.secondary,
                mt: 0.25,
                textAlign: isUser ? 'right' : 'left',
              }}
            />
          )}
        </Box>
      </Box>
    </Fade>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

function toMessage(row: ProductChatMessage): Message {
  return {
    id: row.id,
    text: row.content,
    sender: row.role === 'user' ? 'user' : 'bot',
    timestamp: new Date(row.createdAt),
  };
}

function errorText(error: any): string {
  return error?.response?.data?.message ?? 'Sorry, something went wrong. Please try again.';
}

interface ChatWidgetProps {
  // The conversation with Sila this widget holds - see chatService.
  chatUrl: string;
  productName?: string;
}

export default function ChatWidget({ chatUrl, productName }: ChatWidgetProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // Oldest first, the way they are read.
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const pageRef = useRef(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const streamIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Loading older messages prepends them, which must not scroll to the bottom.
  const scrollToEndRef = useRef(true);

  // Clear streaming interval on unmount
  useEffect(() => {
    return () => {
      if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
    };
  }, []);

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const page = pageRef.current + 1;
      const rows = await chatService.getProductChatMessages({ chatUrl, page, size: PAGE_SIZE });
      pageRef.current = page;
      setHasMore(rows.length === PAGE_SIZE);
      scrollToEndRef.current = page === 1;
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const older = rows.filter((row) => !known.has(row.id)).reverse().map(toMessage);
        return [...older, ...prev];
      });
      setLoaded(true);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { id: generateId(), text: errorText(error), sender: 'bot', timestamp: new Date() },
      ]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // The conversation is fetched the first time the chat is opened, not with the page.
  useEffect(() => {
    if (open && !loaded && !isLoadingHistory) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { mutate: sendChatMessage } = useSendProductChatMessage(
    (data) => {
      setIsTyping(false);
      scrollToEndRef.current = true;

      const msgId = generateId();
      const timestamp = new Date();
      const words = data.split(' ');
      let wordIdx = 0;

      // Insert an empty placeholder bubble immediately (no timestamp yet)
      setMessages((prev) => [
        ...prev,
        { id: msgId, text: '', sender: 'bot' as const, timestamp, isStreaming: true },
      ]);

      // Reveal one word at a time
      streamIntervalRef.current = setInterval(() => {
        wordIdx++;
        const displayText = words.slice(0, wordIdx).join(' ');
        const done = wordIdx >= words.length;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === msgId
              ? { ...msg, text: displayText, isStreaming: !done }
              : msg
          )
        );

        if (done) {
          clearInterval(streamIntervalRef.current!);
          streamIntervalRef.current = null;
        }
      }, 45); // ~22 words/sec — feels natural
    },
    (error) => {
      const errorMessage: Message = {
        id: generateId(),
        text: errorText(error),
        sender: 'bot',
        timestamp: new Date(),
        isBlocked: error?.response?.data?.code === 'message-blocked',
      };
      setIsTyping(false);
      scrollToEndRef.current = true;
      setMessages((prev) => [...prev, errorMessage]);
    }
  );

  const { mutate: clearChat, isLoading: isClearing } = useClearProductChat(
    () => {
      if (streamIntervalRef.current) clearInterval(streamIntervalRef.current);
      streamIntervalRef.current = null;
      setMessages([]);
      setHasMore(false);
      pageRef.current = 0;
    },
    (error) => snackbarService.showSnackbar(errorText(error), 'error')
  );

  // Auto-scroll to latest message
  useEffect(() => {
    if (open && scrollToEndRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping, open]);

  // Focus input when chat opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const handleToggle = () => setOpen((prev) => !prev);

  const sendMessage = () => {
    const text = inputValue.trim();
    if (!text) return;

    const userMessage: Message = {
      id: generateId(),
      text,
      sender: 'user',
      timestamp: new Date(),
    };

    scrollToEndRef.current = true;
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    sendChatMessage({ chatUrl, message: text });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1300,
      }}
    >
      {/* ── Chat popup ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.25s ease, transform 0.25s ease',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: { xs: 'calc(100vw - 48px)', sm: 400, md: 600 },
            height: 600,
            borderRadius: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 8px 32px rgba(7,59,112,0.18)',
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={1.5}
            sx={{ bgcolor: colors.primary }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: colors.white,
                  border: `1px solid rgba(255,255,255,0.3)`,
                }}
              >
                <SmartToyOutlinedIcon sx={{ fontSize: 18, color: colors.primary }} />
              </Avatar>
              <Box>
                <CustomText
                  text="Sila"
                  fontSize={14}
                  fontWeight={600}
                  sx={{ color: colors.white, lineHeight: 1.2 }}
                />
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      bgcolor: colors.success,
                    }}
                  />
                  <CustomText
                    text={productName ?? 'Online'}
                    fontSize={11}
                    fontWeight={600}
                    sx={{ color: 'rgba(255,255,255,0.75)' }}
                  />
                </Box>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={0.5}>
              <IconButton
                onClick={() => clearChat(chatUrl)}
                disabled={isClearing || isTyping || messages.length === 0}
                size="small"
                title="Clear chat"
                sx={{
                  color: colors.white,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                  '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleToggle}
                size="small"
                sx={{
                  color: colors.white,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              px: 1.5,
              pt: 1.5,
              pb: 0.5,
              bgcolor: colors.background,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: colors.border,
                borderRadius: 2,
              },
            }}
          >
            {isLoadingHistory && (
              <Box display="flex" justifyContent="center" mb={1.5}>
                <CircularProgress size={18} sx={{ color: colors.secondary }} />
              </Box>
            )}

            {hasMore && !isLoadingHistory && (
              <Box display="flex" justifyContent="center" mb={1.5}>
                <Box
                  component="button"
                  onClick={loadHistory}
                  sx={{
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: colors.secondary,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Load earlier messages
                </Box>
              </Box>
            )}

            {loaded && !isLoadingHistory && messages.length === 0 && (
              <CustomText
                text="Ask Sila anything about this product."
                fontSize={13}
                sx={{ color: colors.secondary, textAlign: 'center', mt: 2 }}
              />
            )}

            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <Fade in timeout={200}>
                <Box display="flex" mb={1.5}>
                  <Box
                    sx={{
                      bgcolor: colors.topBackground,
                      borderRadius: '14px 14px 14px 4px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}
                  >
                    <TypingIndicator />
                  </Box>
                </Box>
              </Fade>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input area */}
          <Box
            display="flex"
            alignItems="flex-end"
            gap={1}
            px={1.5}
            py={1.25}
            sx={{
              borderTop: `1px solid ${colors.border}`,
              bgcolor: colors.background,
            }}
          >
            <TextField
              inputRef={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message…"
              multiline
              maxRows={3}
              size="small"
              fullWidth
              disabled={isTyping}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: 15,
                  bgcolor: colors.topBackground,
                  '& fieldset': { border: 'none' },
                },
              }}
            />
            <IconButton
              onClick={sendMessage}
              disabled={!inputValue.trim() || isTyping}
              sx={{
                bgcolor: colors.primary,
                color: colors.white,
                width: 36,
                height: 36,
                flexShrink: 0,
                '&:hover': { bgcolor: colors.dark },
                '&.Mui-disabled': {
                  bgcolor: colors.border,
                  color: colors.secondary,
                },
                transition: 'background-color 0.2s',
              }}
            >
              {isTyping
                ? <CircularProgress size={16} sx={{ color: colors.secondary }} />
                : <SendIcon sx={{ fontSize: 17 }} />
              }
            </IconButton>
          </Box>
        </Paper>
      </Box>

      {/* ── FAB trigger button ──────────────────────────────────────────── */}
      <Fade in={!open} timeout={200} unmountOnExit>
        <Fab
          onClick={handleToggle}
          sx={{
            bgcolor: colors.primary,
            color: colors.white,
            width: 56,
            height: 56,
            boxShadow: '0 4px 16px rgba(7,59,112,0.35)',
            '&:hover': {
              bgcolor: colors.dark,
              transform: 'scale(1.07)',
            },
            transition: 'transform 0.2s, background-color 0.2s',
          }}
        >
          <ChatIcon />
        </Fab>
      </Fade>
    </Box>
  );
}
